import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Search, Filter, X, AlertTriangle, ChevronLeft, ChevronRight, Copy, Check, Loader2, Send, Wrench, DollarSign, Repeat } from 'lucide-react';
import api from '../../services/api';

const fmtEur = (cents) => (cents == null ? '—' : `${(cents / 100).toFixed(2)} €`);
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', { timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const STATUS_CFG = {
  scheduled: { label: 'Programmé', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  processing: { label: 'En cours', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  paid: { label: 'Payé', cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' },
  provider_extended: { label: 'Prolongé', cls: 'bg-violet-500/15 text-violet-300 border-violet-500/30' },
  success: { label: 'Réussi', cls: 'bg-green-500/15 text-green-300 border-green-500/30' },
  payment_failed: { label: 'Échec paiement', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  verification_failed: { label: 'À vérifier', cls: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  cancelled: { label: 'Annulé', cls: 'bg-gray-500/15 text-gray-300 border-gray-500/30' },
};
const badgeCfg = (map, v, fallback = '—') => map[v] || { label: v || fallback, cls: 'bg-[#1A1A1D] text-gray-400 border-[#2A2A2E]' };
const PROVIDER_CFG = {
  pending: { label: 'En attente', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  processing: { label: 'En cours', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  committing: { label: 'En cours', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  confirmed: { label: 'Confirmé', cls: 'bg-green-500/15 text-green-300 border-green-500/30' },
  verification_failed: { label: 'À vérifier', cls: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  failed: { label: 'Erreur', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
};
const COMMISSION_CFG = {
  pending: { label: 'En attente', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  confirmed: { label: 'Confirmée', cls: 'bg-green-500/15 text-green-300 border-green-500/30' },
  not_applicable: { label: '—', cls: 'bg-[#1A1A1D] text-gray-500 border-[#2A2A2E]' },
  error: { label: 'Erreur', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
};
const RECOVERY_CFG = {
  not_sent: { label: 'À envoyer', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  sent: { label: 'Email envoyé', cls: 'bg-teal-500/15 text-teal-300 border-teal-500/30' },
  error: { label: 'Erreur', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

const Badge = ({ cfg, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.cls}`}>{cfg.label}</span>
);

const Kpi = ({ label, value, accent, testid }) => (
  <div data-testid={testid} className="bg-[#121214] border border-[#1F1F23] rounded-xl p-4">
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className={`text-2xl font-bold ${accent || 'text-white'}`}>{value}</p>
  </div>
);

export default function RenewalsManagement() {
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, limit: 20 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', status: '', renewal_type: '', attention: '' });
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  const loadStats = useCallback(async () => {
    try { const r = await api.get('/admin/renewals/stats'); setStats(r.data); } catch { /* noop */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.renewal_type) params.renewal_type = filters.renewal_type;
      if (filters.attention) params.attention = 'true';
      const r = await api.get('/admin/renewals', { params });
      setData(r.data);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    const t = setInterval(() => { loadStats(); loadList(); }, 60000);
    return () => clearInterval(t);
  }, [loadStats, loadList]);

  const openDetail = async (id) => {
    setDetailLoading(true); setDetail({ id });
    try { const r = await api.get(`/admin/renewals/${id}`); setDetail(r.data); } catch { setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const refreshAll = () => { loadStats(); loadList(); };

  const runAction = async (kind, id, extra) => {
    setActionLoading(kind);
    try {
      let url = '';
      if (kind === 'create-recovery') url = `/admin/renewals/${id}/create-recovery`;
      else if (kind === 'send-recovery') url = `/admin/renewals/${id}/send-recovery${extra?.force ? '?force_resend=true' : ''}`;
      else if (kind === 'retry-handoff') url = `/admin/renewals/${id}/retry-provider-handoff`;
      else if (kind === 'retry-commission') url = `/admin/renewals/${id}/retry-commission`;
      const r = await api.post(url);
      const st = r.data?.status;
      if (st === 'not_eligible_yet') window.alert(`Recovery pas encore éligible. Éligible le ${fmtDate(r.data.eligible_at)}.`);
      else if (st === 'no_longer_needed') window.alert('Le renouvellement a déjà été régularisé.');
      else if (st === 'already_sent') window.alert('Offre déjà envoyée.');
      // Le drawer reste sur l'ordre affiché (failed order pour create/send depuis un failed).
      const refreshId = extra?.refreshId || (kind === 'create-recovery' && r.data?.recovery_of_order_id) || id;
      await openDetail(refreshId);
      refreshAll();
    } catch (e) {
      const d = e?.response?.data?.detail;
      window.alert(typeof d === 'object' ? (d.message || 'Action impossible.') : (d || 'Action impossible.'));
    } finally { setActionLoading(null); setConfirmModal(null); }
  };

  const askConfirm = (message, onOk) => setConfirmModal({ message, onOk });

  const resetFilters = () => { setFilters({ search: '', status: '', renewal_type: '', attention: '' }); setPage(1); };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="renewals-management">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Repeat className="h-6 w-6 text-[#FF6B35]" /> Renouvellements</h1>
          <p className="text-sm text-gray-500">Supervision des renouvellements automatiques et manuels</p>
        </div>
        <button data-testid="renewals-refresh-btn" onClick={refreshAll} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B35] text-white text-sm font-medium hover:bg-[#e85f2d] transition-colors self-start">
          <RefreshCw className="h-4 w-4" /> Actualiser
        </button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="renewals-kpis">
        <Kpi testid="kpi-upcoming" label="À venir" value={stats?.upcoming ?? '—'} />
        <Kpi testid="kpi-paid" label="Payés" value={stats?.paid ?? '—'} accent="text-cyan-300" />
        <Kpi testid="kpi-success" label="Réussis" value={stats?.success ?? '—'} accent="text-green-300" />
        <Kpi testid="kpi-failed" label="Échecs paiement" value={stats?.payment_failed ?? '—'} accent="text-red-300" />
        <Kpi testid="kpi-provider-errors" label="Erreurs fournisseur" value={stats?.provider_errors ?? '—'} accent="text-orange-300" />
        <Kpi testid="kpi-recovery" label="Recovery éligibles" value={stats?.recovery_eligible ?? '—'} accent="text-amber-300" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Kpi testid="kpi-revenue-today" label="CA renouvelé aujourd'hui" value={fmtEur(stats?.revenue_today_cents)} accent="text-green-300" />
        <Kpi testid="kpi-revenue-month" label="CA renouvelé ce mois" value={fmtEur(stats?.revenue_month_cents)} accent="text-green-300" />
      </div>

      {/* Filters */}
      <div className="bg-[#121214] border border-[#1F1F23] rounded-xl p-4 flex flex-col md:flex-row gap-3 md:items-center flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input data-testid="renewals-search-input" value={filters.search}
            onChange={(e) => { setFilters(f => ({ ...f, search: e.target.value })); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); loadList(); } }}
            placeholder="Email, nom, order id, PaymentIntent, subscription…"
            className="w-full pl-9 pr-3 py-2 bg-[#0A0A0B] border border-[#2A2A2E] rounded-lg text-sm text-white placeholder-gray-600 focus:border-[#FF6B35] outline-none" />
        </div>
        <select data-testid="renewals-filter-status" value={filters.status} onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}
          className="px-3 py-2 bg-[#0A0A0B] border border-[#2A2A2E] rounded-lg text-sm text-white">
          <option value="">Tous les statuts</option>
          {['scheduled', 'processing', 'paid', 'provider_extended', 'success', 'payment_failed', 'verification_failed', 'cancelled'].map(s => <option key={s} value={s}>{STATUS_CFG[s]?.label || s}</option>)}
        </select>
        <select data-testid="renewals-filter-type" value={filters.renewal_type} onChange={(e) => { setFilters(f => ({ ...f, renewal_type: e.target.value })); setPage(1); }}
          className="px-3 py-2 bg-[#0A0A0B] border border-[#2A2A2E] rounded-lg text-sm text-white">
          <option value="">Tous les types</option>
          <option value="auto">Auto</option>
          <option value="manual">Manuel</option>
          <option value="expired_recovery">Recovery</option>
        </select>
        <button data-testid="renewals-filter-attention" onClick={() => { setFilters(f => ({ ...f, attention: f.attention ? '' : 'true' })); setPage(1); }}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border ${filters.attention ? 'bg-red-500/15 text-red-300 border-red-500/40' : 'bg-[#0A0A0B] text-gray-400 border-[#2A2A2E]'}`}>
          <AlertTriangle className="h-4 w-4" /> Attention requise
        </button>
        <button data-testid="renewals-reset-filters" onClick={resetFilters} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-white">
          <X className="h-4 w-4" /> Réinitialiser
        </button>
      </div>

      {/* Table desktop */}
      <div className="hidden lg:block bg-[#121214] border border-[#1F1F23] rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="renewals-table">
          <thead className="bg-[#0A0A0B] text-gray-500 text-xs uppercase">
            <tr>
              {['Client', 'Type', 'Formule', 'Montant', 'Paiement', 'Fournisseur', 'Commission', 'Recovery', 'Date', ''].map(h => <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-500"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-500">Aucun renouvellement</td></tr>
            ) : data.items.map(it => (
              <tr key={it.id} data-testid={`renewal-row-${it.id}`} className={`border-t border-[#1F1F23] hover:bg-[#17171A] ${it.attention_required ? 'bg-red-500/5' : ''}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {it.attention_required && <AlertTriangle className="h-3.5 w-3.5 text-red-400" title={it.attention_reason} />}
                    <div><p className="text-white truncate max-w-[160px]">{it.client?.name || '—'}</p><p className="text-gray-500 text-xs truncate max-w-[160px]">{it.client?.email}</p></div>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-400">{it.renewal_type === 'expired_recovery' ? 'Recovery' : it.renewal_type === 'manual' ? 'Manuel' : 'Auto'}</td>
                <td className="px-4 py-3 text-gray-400">{it.duration_label} · {it.screens}é</td>
                <td className="px-4 py-3 text-white">{fmtEur(it.final_amount_cents)}</td>
                <td className="px-4 py-3"><Badge cfg={badgeCfg(STATUS_CFG, it.status)} testid={`pay-badge-${it.id}`} /></td>
                <td className="px-4 py-3">{it.provider?.job_status ? <Badge cfg={badgeCfg(PROVIDER_CFG, it.provider.job_status)} /> : <span className="text-gray-600">—</span>}</td>
                <td className="px-4 py-3">{it.commission?.status ? <Badge cfg={badgeCfg(COMMISSION_CFG, it.commission.status)} /> : <span className="text-gray-600">—</span>}</td>
                <td className="px-4 py-3">{(it.recovery?.offer_status || it.recovery?.recovery_of_order_id) ? <Badge cfg={badgeCfg(RECOVERY_CFG, it.recovery.offer_status)} /> : <span className="text-gray-600">—</span>}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate(it.created_at)}</td>
                <td className="px-4 py-3"><button data-testid={`detail-btn-${it.id}`} onClick={() => openDetail(it.id)} className="text-[#FF6B35] hover:underline text-xs">Détail</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cards mobile */}
      <div className="lg:hidden space-y-3">
        {loading ? <div className="text-center text-gray-500 py-8"><Loader2 className="h-5 w-5 animate-spin inline" /></div> :
          data.items.map(it => (
            <div key={it.id} data-testid={`renewal-card-${it.id}`} className={`bg-[#121214] border rounded-xl p-4 ${it.attention_required ? 'border-red-500/40' : 'border-[#1F1F23]'}`}>
              <div className="flex justify-between items-start mb-2">
                <div><p className="text-white text-sm">{it.client?.name || it.client?.email}</p><p className="text-gray-500 text-xs">{fmtDate(it.created_at)}</p></div>
                <Badge cfg={badgeCfg(STATUS_CFG, it.status)} />
              </div>
              <div className="flex justify-between text-sm text-gray-400 mb-3">
                <span>{it.duration_label} · {it.screens}é</span><span className="text-white font-medium">{fmtEur(it.final_amount_cents)}</span>
              </div>
              <div className="flex gap-2 flex-wrap mb-3">
                {it.provider?.job_status && <Badge cfg={badgeCfg(PROVIDER_CFG, it.provider.job_status)} />}
                {it.commission?.status && it.commission.status !== 'not_applicable' && <Badge cfg={badgeCfg(COMMISSION_CFG, it.commission.status)} />}
              </div>
              <button data-testid={`detail-btn-${it.id}`} onClick={() => openDetail(it.id)} className="w-full py-2 rounded-lg bg-[#1A1A1D] text-[#FF6B35] text-sm font-medium">Voir le détail</button>
            </div>
          ))}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>{data.total} renouvellement(s)</span>
        <div className="flex items-center gap-2">
          <button data-testid="page-prev" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="p-2 rounded-lg bg-[#121214] border border-[#1F1F23] disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
          <span className="text-gray-400">Page {data.page} / {data.pages || 1}</span>
          <button data-testid="page-next" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} className="p-2 rounded-lg bg-[#121214] border border-[#1F1F23] disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      {detail && <DetailDrawer detail={detail} loading={detailLoading} onClose={() => setDetail(null)} onAction={runAction} askConfirm={askConfirm} actionLoading={actionLoading} copied={copied} setCopied={setCopied} />}

      {confirmModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="confirm-modal">
          <div className="bg-[#121214] border border-[#2A2A2E] rounded-xl p-5 max-w-sm w-full">
            <p className="text-white text-sm mb-4">{confirmModal.message}</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmModal(null)} className="px-4 py-2 rounded-lg text-gray-400 text-sm">Annuler</button>
              <button data-testid="confirm-ok-btn" onClick={confirmModal.onOk} className="px-4 py-2 rounded-lg bg-[#FF6B35] text-white text-sm font-medium">Confirmer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-[#1A1A1D] last:border-0">
      <span className="text-gray-500 text-xs">{label}</span>
      <span className={`text-sm text-right text-gray-200 ${mono ? 'font-mono text-xs' : ''}`}>{value ?? '—'}</span>
    </div>
  );
}

function DetailDrawer({ detail, loading, onClose, onAction, askConfirm, actionLoading, copied, setCopied }) {
  const d = detail;
  const pi = d?.payment?.intent_id;
  const rec = d?.recovery || {};
  const isRecovery = d?.renewal_type === 'expired_recovery';
  const recTargetId = isRecovery ? d?.id : rec.order_id; // ordre recovery à envoyer
  const hasRecoveryChild = !isRecovery && !!rec.order_id;
  const canCreateRecovery = d?.status === 'payment_failed' && d?.renewal_type === 'auto' && !rec.order_id;
  const canSend = !!recTargetId && rec.status === 'scheduled' && rec.offer_status !== 'sent';
  const canResend = !!recTargetId && rec.offer_status === 'sent';
  const canRetryHandoff = d?.status === 'paid' && (!d?.provider?.job_id || d?.provider?.handoff_status === 'error');
  const canRetryCommission = d?.status === 'success' && d?.seller?.id && d?.commission?.status !== 'confirmed';
  const email = d?.client?.email;
  const failedId = isRecovery ? null : d?.id; // pour rester sur le failed order après send

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="renewal-detail-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#0A0A0B] border-l border-[#1F1F23] h-full overflow-y-auto p-5 space-y-5">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold text-white">Détail du renouvellement</h2>
          <button data-testid="drawer-close-btn" onClick={onClose} className="text-gray-500 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        {loading ? <div className="py-10 text-center text-gray-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : d && d.status ? (
          <>
            <section className="bg-[#121214] border border-[#1F1F23] rounded-xl p-4">
              <h3 className="text-sm font-semibold text-[#FF6B35] mb-2">Client</h3>
              <Row label="Nom" value={d.client?.name} />
              <Row label="Email" value={d.client?.email} />
              <Row label="Abonnement" value={d.subscription_id} mono />
            </section>

            <section className="bg-[#121214] border border-[#1F1F23] rounded-xl p-4">
              <h3 className="text-sm font-semibold text-[#FF6B35] mb-2">Renouvellement</h3>
              <Row label="Étape" value={d.operational_stage} />
              <Row label="Statut" value={<Badge cfg={badgeCfg(STATUS_CFG, d.status)} testid="drawer-status-badge" />} />
              <Row label="Type" value={d.renewal_type} />
              <Row label="Formule" value={`${d.duration_label} · ${d.screens} écran(s)`} />
              <Row label="Catalogue" value={fmtEur(d.catalog_price_cents)} />
              <Row label="Remise" value={fmtEur(d.discount_cents)} />
              <Row label="À payer" value={fmtEur(d.final_amount_cents)} />
            </section>

            <section className="bg-[#121214] border border-[#1F1F23] rounded-xl p-4" data-testid="drawer-payment">
              <h3 className="text-sm font-semibold text-[#FF6B35] mb-2">Paiement</h3>
              <Row label="Statut" value={<Badge cfg={badgeCfg(STATUS_CFG, d.status)} />} />
              <Row label="Payé le" value={fmtDate(d.paid_at)} />
              <Row label="Échec le" value={fmtDate(d.payment_failed_at)} />
              {pi && (
                <div className="flex justify-between items-center py-1.5">
                  <span className="text-gray-500 text-xs">PaymentIntent</span>
                  <button data-testid="copy-pi-btn" onClick={() => { navigator.clipboard?.writeText(pi); setCopied(true); setTimeout(() => setCopied(false), 1500); }} className="inline-flex items-center gap-1 font-mono text-xs text-gray-200 hover:text-[#FF6B35]">
                    {pi} {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </button>
                </div>
              )}
              {d.payment?.error_code && <Row label="Erreur" value={d.payment.error_code} />}
              <p className="text-[11px] text-gray-600 mt-2">Paiement manuel : non activé</p>
            </section>

            <section className="bg-[#121214] border border-[#1F1F23] rounded-xl p-4" data-testid="drawer-provider">
              <h3 className="text-sm font-semibold text-[#FF6B35] mb-2">Fournisseur</h3>
              <Row label="Handoff" value={d.provider?.handoff_status} />
              <Row label="Job" value={d.provider?.job_status ? <Badge cfg={badgeCfg(PROVIDER_CFG, d.provider.job_status)} /> : '—'} />
              <Row label="Tentatives" value={d.provider?.attempts} />
              <Row label="Ligne" value={d.provider?.target_line_id} mono />
              <Row label="Exp. confirmée" value={fmtDate(d.provider?.confirmed_expiration)} />
              {d.provider?.last_error && <Row label="Erreur" value={d.provider.last_error} />}
            </section>

            {d.seller && (
              <section className="bg-[#121214] border border-[#1F1F23] rounded-xl p-4" data-testid="drawer-commission">
                <h3 className="text-sm font-semibold text-[#FF6B35] mb-2">Commission vendeur</h3>
                <Row label="Vendeur" value={d.seller?.name || d.seller?.email} />
                <Row label="Statut" value={d.commission?.status ? <Badge cfg={badgeCfg(COMMISSION_CFG, d.commission.status)} /> : '—'} />
                <Row label="Palier" value={d.commission?.tier} />
                <Row label="Commission" value={fmtEur(d.commission?.amount_cents)} />
              </section>
            )}

            <section className="bg-[#121214] border border-[#1F1F23] rounded-xl p-4" data-testid="drawer-recovery">
              <h3 className="text-sm font-semibold text-[#FF6B35] mb-2">Recovery J+2</h3>
              {d.payment_failed_at && <Row label="Éligible le" value={fmtDate(rec.eligible_at)} />}
              <Row label="Recovery order" value={rec.order_id} mono />
              {rec.recovery_of_order_id && <Row label="Source (failed)" value={rec.recovery_of_order_id} mono />}
              {(hasRecoveryChild || isRecovery) && <>
                <Row label="Statut" value={rec.status ? <Badge cfg={badgeCfg(STATUS_CFG, rec.status)} testid="recovery-child-status" /> : '—'} />
                <Row label="Catalogue" value={fmtEur(rec.catalog_price_cents)} />
                <Row label="Remise" value={fmtEur(rec.discount_cents)} />
                <Row label="À régler" value={fmtEur(rec.final_amount_cents)} />
                <Row label="Payé le" value={fmtDate(rec.paid_at)} />
              </>}
              <Row label="Offre" value={rec.offer_status ? <Badge cfg={badgeCfg(RECOVERY_CFG, rec.offer_status)} testid="recovery-offer-badge" /> : '—'} />
              <Row label="Envoyée le" value={fmtDate(rec.offer_sent_at)} />
            </section>

            {Array.isArray(d.timeline) && d.timeline.length > 0 && (
              <section className="bg-[#121214] border border-[#1F1F23] rounded-xl p-4" data-testid="drawer-timeline">
                <h3 className="text-sm font-semibold text-[#FF6B35] mb-2">Chronologie</h3>
                <div className="space-y-1.5">
                  {d.timeline.map((e, i) => (
                    <div key={i} className="flex gap-2 text-xs"><span className="text-gray-500 whitespace-nowrap">{fmtDate(e.at)}</span><span className="text-gray-300">{e.label}</span></div>
                  ))}
                </div>
              </section>
            )}

            {/* Actions */}
            <section className="space-y-2" data-testid="drawer-actions">
              {canCreateRecovery && (
                <button data-testid="action-create-recovery" disabled={actionLoading} onClick={() => onAction('create-recovery', d.id)}
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 text-sm font-medium">
                  {actionLoading === 'create-recovery' ? <Loader2 className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4" />} Créer l'offre recovery
                </button>
              )}
              {canSend && (
                <button data-testid="action-send-recovery" disabled={actionLoading} onClick={() => askConfirm(`Envoyer l'offre de récupération de 10 € à ${email} ?`, () => onAction('send-recovery', recTargetId, { refreshId: failedId || recTargetId }))}
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-teal-500/15 text-teal-300 border border-teal-500/30 text-sm font-medium">
                  {actionLoading === 'send-recovery' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer l'offre -10 €
                </button>
              )}
              {canResend && (
                <button data-testid="action-resend-recovery" disabled={actionLoading} onClick={() => askConfirm(`Renvoyer l'offre de récupération à ${email} ?`, () => onAction('send-recovery', recTargetId, { force: true, refreshId: failedId || recTargetId }))}
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#1A1A1D] text-gray-300 border border-[#2A2A2E] text-sm font-medium">
                  <Send className="h-4 w-4" /> Renvoyer l'email
                </button>
              )}
              {canRetryHandoff && (
                <button data-testid="action-retry-handoff" disabled={actionLoading} onClick={() => askConfirm('Réparer le handoff fournisseur (aucun débit, aucun appel GoldenOTT) ?', () => onAction('retry-handoff', d.id))}
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-violet-500/15 text-violet-300 border border-violet-500/30 text-sm font-medium">
                  {actionLoading === 'retry-handoff' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />} Réparer le handoff fournisseur
                </button>
              )}
              {canRetryCommission && (
                <button data-testid="action-retry-commission" disabled={actionLoading} onClick={() => askConfirm('Réparer la commission vendeur (aucun débit, aucun appel GoldenOTT) ?', () => onAction('retry-commission', d.id))}
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-green-500/15 text-green-300 border border-green-500/30 text-sm font-medium">
                  {actionLoading === 'retry-commission' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />} Réparer la commission
                </button>
              )}
            </section>
          </>
        ) : <p className="text-gray-500 text-sm">Introuvable.</p>}
      </div>
    </div>
  );
}
